import React, {
   CSSProperties,
   ReactNode,
   useCallback,
   useEffect,
   useLayoutEffect,
   useState
} from 'react';
import { reactNodeIsOfType } from '../../../utils';
import { DEFAULT_LABEL_PANEL_GROUP_MAX_LABEL_WIDTH, LabelPanelGroup } from '../labelpanelgroup';
import { useContainerBreakpoints } from '../../../hooks';
import { VerticalPanel } from '../verticalpanel';
import { PaddingType } from '../panel';
import { debounce } from 'lodash';

export type LabelMode = 'inline' | 'vertical' | 'responsive';

/**
 * {@docCategory LabelPanel}
 */
export interface LabelPanelProps {

   /**
    * Whether or not the labels layout inline, vertical, or responsive based on width
    *
    * @defaultvalue 'responsive'
    */
   labelMode?: LabelMode;

   /**
    * Whether or not to horizontal fill label content
    *
    * @defaultvalue false
    */
   horizontalFillLabelContent?: boolean;

   /**
    * Whether or not to force a large vertical gap size.  This is useful for situations where you
    * have labeled fields and need a larger vertical gap size.
    */
   useLargeVerticalGapSize?: boolean;

   /**
    * Determines if the panel will fill it's parent containers height.
    *
    * @defaultvalue false
    */
   verticalFill?: boolean;

   /**
    * Determines if the panel has padding around the content.
    *
    * @defaultvalue true
    */
   padding?: PaddingType;

   /**
    * Determines if the panel has left padding
    *
    * @defaultvalue false
    */
   paddingLeft?: PaddingType;

   /**
    * Determines if the panel has right padding
    *
    * @defaultvalue false
    */
   paddingRight?: PaddingType;

   /**
    * Determines if the panel has top padding
    *
    * @defaultvalue false
    */
   paddingTop?: PaddingType;

   /**
    * Determines if the panel has bottom padding
    *
    * @defaultvalue false
    */
   paddingBottom?: PaddingType;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Max Label Width Callback - Keep up with the current max label width for the first
    * column of labels
    */
   onMaxLabelWidthChange?: (width: number, isInlineLabel?: boolean) => void;
}

const MAX_INLINE_LABEL_WIDTH = DEFAULT_LABEL_PANEL_GROUP_MAX_LABEL_WIDTH;
const MIN_INLINE_LABEL_WIDTH = 100;
const INLINE_LABEL_BREAKPOINT = 250 + MIN_INLINE_LABEL_WIDTH + 32; // Field width + min label width + 32 for 'medium' padding on the LabelPanel

export const LabelPanel = (props: React.PropsWithChildren<LabelPanelProps>) => {

   const { labelMode, onMaxLabelWidthChange, horizontalFillLabelContent } = props;

   const noInlineLabels = labelMode === 'vertical';
   const [dimensionsRef, dimensions, currentBreakpoint] = useContainerBreakpoints();
   const [inlineLabel, setInlineLabel] = useState<boolean>(!noInlineLabels);
   const [inlineLabelWidth, setInlineLabelWidth] = useState<number | undefined>();

   // eslint-disable-next-line react-hooks/exhaustive-deps
   const switchLabelMode = useCallback(debounce((inline: boolean, parentWidth: number) => {
      setInlineLabel(inline);
      if (inline) {
         const width = calculateInlineLabelWidth(parentWidth);
         setInlineLabelWidth(width);
         if (onMaxLabelWidthChange) {
            onMaxLabelWidthChange(width, true);
         }
      }
      else {
         if (onMaxLabelWidthChange) {
            onMaxLabelWidthChange(0, false);
         }
      }
   }, 150),[onMaxLabelWidthChange]);

   useLayoutEffect(() => {
      if (!noInlineLabels && dimensionsRef && dimensions && dimensions.width > 0) {
         const isInline = labelMode==='inline' || (dimensions.width > INLINE_LABEL_BREAKPOINT);
         switchLabelMode(isInline, dimensions.width);
      }
   }, [dimensions, noInlineLabels, dimensionsRef, labelMode, switchLabelMode]);

   const padding = props.padding === undefined ? true : props.padding;

   return (
      <VerticalPanel
         className={`uf-label-panel ${props.className || ''}`}
         style={props.style}
         verticalFill={props.verticalFill || false}
         horizontalFill={true}
         scroll={false}  // relies on parent to provide scrolling
         padding={padding}
         paddingTop={props.paddingTop}
         paddingBottom={props.paddingBottom}
         paddingLeft={props.paddingLeft}
         paddingRight={props.paddingRight}
         gap={(!inlineLabel || props.useLargeVerticalGapSize) ? 'medium' : false}
         ref={dimensionsRef}
      >
         {!dimensions ? null : addPanelGroups(props.children,
                                              inlineLabel,
                                              props.horizontalFillLabelContent,
                                              props.useLargeVerticalGapSize,
                                              inlineLabelWidth)}
      </VerticalPanel>
   );
};

interface InlinableType {
   inline?: boolean;
   horizontalFillLabelContent?: boolean;
   maxLabelWidth?: number;
   useLargeVerticalGapSize?: boolean;
}

const addPanelGroups = (children: ReactNode | undefined,
                        inline: boolean,
                        horizontalFillLabelContent: boolean | undefined,
                        useLargeVerticalGapSize: boolean | undefined,
                        inlineLabelWidth?: number) => {

   if (children === undefined) {
      return children;
   }

   const labelWidthForInline = (inline ? inlineLabelWidth : undefined);

   const array = React.Children.toArray(children);
   const count = array.length;
   const result: ReactNode[] = [];

   let notInPanelGroup: ReactNode[] = [];
   let numPanelGroups = 0;

   array.forEach(
      (child, index) => {
         if (reactNodeIsOfType(child, LabelPanelGroup)) {
            if (notInPanelGroup.length > 0) {
               const PanelGroup = <LabelPanelGroup
                  inline={inline}
                  maxLabelWidth={labelWidthForInline}
                  vertical={true}
                  horizontalFillLabelContent={horizontalFillLabelContent}
                  useLargeVerticalGapSize={useLargeVerticalGapSize}
                  key={++numPanelGroups}
               >
                  {notInPanelGroup}
               </LabelPanelGroup>;

               result.push(PanelGroup);
               notInPanelGroup = [];
            }

            if (React.isValidElement<InlinableType>(child)) {
               result.push(React.cloneElement(child,
                                              {
                                                 inline: inline,
                                                 horizontalFillLabelContent: horizontalFillLabelContent,
                                                 useLargeVerticalGapSize: useLargeVerticalGapSize,
                                                 maxLabelWidth: labelWidthForInline,
                                                 key: ++numPanelGroups
                                              }));
            }
         }
         else {
            notInPanelGroup.push(child);
         }
      }
   );

   if (notInPanelGroup.length > 0) {

      const PanelGroup = <LabelPanelGroup
         inline={inline}
         maxLabelWidth={labelWidthForInline}
         vertical={true}
         horizontalFillLabelContent={horizontalFillLabelContent}
         useLargeVerticalGapSize={useLargeVerticalGapSize}
         key={++numPanelGroups}
      >
         {notInPanelGroup}
      </LabelPanelGroup>;

      result.push(PanelGroup);
   }

   return result;
};

const calculateInlineLabelWidth = (width: number) => {
   const distanceFromBreakpoint = width - (INLINE_LABEL_BREAKPOINT - 60);
   if (distanceFromBreakpoint > MAX_INLINE_LABEL_WIDTH) {
      return MAX_INLINE_LABEL_WIDTH;
   }
   else if (distanceFromBreakpoint < MIN_INLINE_LABEL_WIDTH) {
      return MIN_INLINE_LABEL_WIDTH;
   }
   return distanceFromBreakpoint - (distanceFromBreakpoint / 4);
};
